import { ReactNode } from "react";

import FiltersMobileTriggerButton from "components/Filters/MobileTriggerButton";
import SearchInput from "components/SearchInput";
import ViewCustomizationContextProvider from "components/ViewCustomization/Context";
import Box from "ds/components/Box";
import PageInfoHeader from "ds/components/PageInfoHeader";
import SegmentedControl from "ds/components/SegmentedControl";
import Select from "ds/components/Select";
import SelectInput from "ds/components/Select/Input";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageResources } from "hooks/useAnalytics/pages/resources";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import {
  ANSIBLE_GROUP_BY_OPTIONS,
  ANSIBLE_INITIAL_ITEMS_SETTINGS,
  ANSIBLE_LIST_VIEW_ITEMS_SETTINGS_KEY,
  FILTERS_ORDER_SETTINGS_KEY,
  LAYOUT_MODE_OPTIONS,
} from "./constants";
import styles from "./styles.module.css";
import { AnsibleGroupBy, LayoutMode } from "./types";

type ConfigManagementPageLayoutProps = {
  children: ReactNode;
  setLayoutMode: (newValue: LayoutMode) => void;
  layoutMode: LayoutMode;
  hideSearch?: boolean;
  loading?: boolean;
  groupBy: AnsibleGroupBy;
  setGroupBy: (groupBy: AnsibleGroupBy) => void;
};

const ConfigManagementPageLayout = ({
  children,
  layoutMode,
  setLayoutMode,
  hideSearch,
  loading,
  groupBy,
  setGroupBy,
}: ConfigManagementPageLayoutProps) => {
  const { accountName } = useTypedContext(AccountContext);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageResources.ConfigurationManagement,
  });

  useTitle(`Configuration management · ${accountName}`);

  const handleGroupByChange = (value: AnsibleGroupBy) => {
    trackSegmentAnalyticsEvent?.("Group by changed", { groupBy: value });
    setGroupBy(value);
  };

  return (
    <ViewCustomizationContextProvider
      localStorageKey={ANSIBLE_LIST_VIEW_ITEMS_SETTINGS_KEY}
      initialItems={ANSIBLE_INITIAL_ITEMS_SETTINGS}
    >
      <PageInfoHeader title="Configuration management">
        {!hideSearch && (
          <>
            <SearchInput
              disabled={loading}
              placeholder="Search by host or stack name"
              filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
            />
            <Box>
              <Select
                value={groupBy}
                items={ANSIBLE_GROUP_BY_OPTIONS}
                onChange={handleGroupByChange}
                renderInput={(props) => (
                  <SelectInput
                    {...props}
                    renderValue={() => (
                      <Typography variant="p-t7" tag="span">
                        Grouped by: {groupBy}
                      </Typography>
                    )}
                  />
                )}
              />
            </Box>

            <SegmentedControl
              optionClassName={styles.layoutSwitchOption}
              onChange={(value) => {
                trackSegmentAnalyticsEvent?.("View Changed", { type: value });
                setLayoutMode(value);
              }}
              value={layoutMode}
              options={LAYOUT_MODE_OPTIONS}
            />
          </>
        )}
        <FiltersMobileTriggerButton />
      </PageInfoHeader>

      {children}
    </ViewCustomizationContextProvider>
  );
};

export default ConfigManagementPageLayout;
